import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "../../styles/pages/Notification/NotificationsPage.module.css";

import { Title } from "../../components/UI/Custom/Title";
import { LoadingCircle } from "../../components/UI/Custom/LoadingCircle";
import { Notification as Toast } from "../../components/UI/Notifications";
import { handleError } from "../../utils/custom/errorHandler";
import { formatDateTime } from "../../utils/custom/formatters";
import { DropdownSelector } from "../../components/UI/DropdownSelector";

import { LangContext } from "../../context/LangContext";
import { ThemeContext } from "../../context/ThemeContext";

import {
    listNotifications,
    markNotificationRead,
    markAllNotificationsRead,
} from "../../services/notifications/notificationsApi";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const translations = {
    ru: {
        title: "Уведомления",
        noItems: "Уведомлений не найдено.",
        previous: "Пред.",
        next: "След.",
        perPage: (n) => `${n} / стр.`,
        readAll: "Прочитать все",
        unreadOnly: "Только непрочитанные",
        total: "всего",
    },
    en: {
        title: "Notifications",
        noItems: "No notifications found.",
        previous: "Prev.",
        next: "Next",
        perPage: (n) => `${n} / page`,
        readAll: "Mark all as read",
        unreadOnly: "Unread only",
        total: "total",
    },
};

function iconByNotification(n) {
    const action = String(n?.action || "").toLowerCase();
    const type = String(n?.related_entity_type || "").toLowerCase();

    if (action.startsWith("user.") || action.includes("registered") || type === "lead") return <PersonIcon />;
    if (action.includes("deposit_method")) return <PaymentOutlinedIcon />;
    if (
        action.includes("transaction") ||
        action.includes("deposit") ||
        action.includes("withdraw") ||
        action.includes("payment")
    )
        return <AttachMoneyOutlinedIcon />;
    if (action.startsWith("order.") || action.includes("open_deal") || action.includes("deal") || type === "order")
        return <TrendingUpOutlinedIcon />;
    if (action.includes("comment") || action.includes("chat") || type === "chat") return <ChatBubbleOutlineOutlinedIcon />;
    if (action.includes("history") || action.includes("changed") || action.includes("updated")) return <HistoryOutlinedIcon />;

    return <InfoOutlinedIcon />;
}

export const NotificationsPage = () => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;

    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [unreadOnly, setUnreadOnly] = useState(false);

    const perPageOptions = useMemo(
        () => [
            { label: t.perPage(15), value: 15 },
            { label: t.perPage(25), value: 25 },
            { label: t.perPage(50), value: 50 },
            { label: t.perPage(100), value: 100 },
        ],
        [t]
    );

    const load = useCallback(async () => {
        setIsLoading(true);
        setErrors({});

        try {
            const result = await listNotifications({
                page: currentPage,
                perPage: itemsPerPage,
                unreadOnly,
            });

            const { data = [], meta = {} } = result || {};

            setItems(Array.isArray(data) ? data : []);
            setCurrentPage(meta.current_page || currentPage);
            setTotalPages(meta.last_page || 1);
            setTotalItems(meta.total ?? 0);
        } catch (e) {
            handleError(e?.response?.data || e, setErrors);
            setItems([]);
            setTotalPages(1);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemsPerPage, unreadOnly]);

    useEffect(() => {
        load();
    }, [load]);

    const handleItemsPerPageChange = (name, value) => {
        const v = parseInt(value, 10);
        if (!Number.isNaN(v)) {
            setItemsPerPage(v);
            setCurrentPage(1);
        }
    };

    const handlePreviousPage = () => setCurrentPage((p) => Math.max(1, p - 1));
    const handleNextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

    const handleOpen = async (n) => {
        try {
            if (!n?.read_at) {
                await markNotificationRead(n.id);
                setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
            }
        } catch (e) {
            // do not block navigation if API fails
        }

        if (n?.url) {
            navigate(n.url);
        }
    };

    const handleReadAll = async () => {
        try {
            await markAllNotificationsRead();
            const nowIso = new Date().toISOString();

            // update current list instantly
            setItems((prev) => prev.map((x) => (!x.read_at ? { ...x, read_at: nowIso } : x)));

            // refresh list (important if unreadOnly=true)
            load();
        } catch (e) {
            handleError(e?.response?.data || e, setErrors);
        }
    };

    return (
        <div className={`${styles.container} ${theme === "dark" ? styles.darkContainer : ""}`}>
            {errors.general && <Toast message={errors.general} type={"error"} />}

            <div className={`${styles.content} ${theme === "dark" ? styles.darkContent : ""}`}>
                <div className={styles.topBar}>
                    <Title h2={t.title} />

                    {/* Controls UNDER title; first control left, others right via CSS */}
                    <div className={styles.controls}>
                        <div className={styles.itemsPerPageSelector}>
                            <DropdownSelector
                                filterName="itemsPerPage"
                                filterKey="itemsPerPage"
                                selectedValue={itemsPerPage}
                                handleFilterChange={handleItemsPerPageChange}
                                options={perPageOptions}
                                hasError={false}
                                errorMessage=""
                            />
                        </div>

                        <label className={`${styles.unreadOnly} ${theme === "dark" ? styles.darkUnreadOnly : ""}`}>
                            <input
                                type="checkbox"
                                checked={unreadOnly}
                                onChange={(e) => {
                                    setUnreadOnly(e.target.checked);
                                    setCurrentPage(1);
                                }}
                            />
                            <span>{t.unreadOnly}</span>
                        </label>

                        <button
                            className={`${styles.markAllBtn} ${theme === "dark" ? styles.darkMarkAllBtn : ""}`}
                            onClick={handleReadAll}
                            disabled={isLoading}
                        >
                            {t.readAll}
                        </button>
                    </div>
                </div>

                {isLoading ? (
                    <div className={styles.loadingBlock}>
                        <LoadingCircle size={30} thickness={1} />
                    </div>
                ) : items.length === 0 ? (
                    <div className={`${styles.empty} ${theme === "dark" ? styles.darkEmpty : ""}`}>{t.noItems}</div>
                ) : (
                    <>
                        <div className={styles.metaLine}>
                            <span>{totalItems ? `${totalItems} ${t.total}` : ""}</span>
                            <span>
                Page {currentPage} / {totalPages}
              </span>
                        </div>

                        <div className={styles.list}>
                            {items.map((n) => {
                                const isUnread = !n.read_at;

                                return (
                                    <div
                                        key={n.id}
                                        className={[
                                            styles.item,
                                            theme === "dark" ? styles.darkItem : "",
                                            isUnread ? styles.itemUnread : "",
                                            theme === "dark" && isUnread ? styles.darkItemUnread : "",
                                        ].join(" ")}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleOpen(n)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handleOpen(n);
                                            }
                                        }}
                                    >
                                        <div className={`${styles.icon} ${theme === "dark" ? styles.darkIcon : ""}`}>{iconByNotification(n)}</div>

                                        <div className={styles.body}>
                                            <div className={styles.messageRow}>
                                                <div className={`${styles.message} ${theme === "dark" ? styles.darkMessage : ""}`}>{n.message}</div>
                                                {isUnread ? <span className={styles.unreadDot} /> : null}
                                            </div>

                                            <div className={`${styles.time} ${theme === "dark" ? styles.darkTime : ""}`}>
                                                {n.created_at ? formatDateTime(n.created_at) : ""}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {totalPages > 1 && (
                            <div className={`${styles.pagination} ${theme === "dark" ? styles.darkPagination : ""}`}>
                                <button
                                    className={`${styles.paginationButton} ${theme === "dark" ? styles.darkPaginationButton : ""}`}
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1 || isLoading}
                                >
                                    <ChevronLeftIcon fontSize="small" />
                                    {t.previous}
                                </button>

                                <div className={styles.pageInfo}>
                                    {currentPage} / {totalPages}
                                </div>

                                <button
                                    className={`${styles.paginationButton} ${theme === "dark" ? styles.darkPaginationButton : ""}`}
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages || isLoading}
                                >
                                    {t.next}
                                    <ChevronRightIcon fontSize="small" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
